import { BillType } from "@redotech/crdb-enums/billing/bill-type";
import { ServiceInvoiceType } from "./service-invoice";

export enum ReturnShippingCatalogProduct {
  ReturnsCoverage = "returns_coverage",
  PackageProtection = "package_protection",
  PackageProtectionPlus = "package_protection_plus",
  FinalSaleReturns = "final_sale_returns",
  Warranty = "warranty",
  Rmr = "rmr",
}

export type BillingCatalogBilledBy = "engine" | "payout" | "legacy_cron";

export interface SoftwareBillingCatalogEntry {
  billType: typeof BillType.Software;
  product: ServiceInvoiceType;
  id: `software:${ServiceInvoiceType}`;
  label: string;
  billedBy: BillingCatalogBilledBy;
  listed: boolean;
  adminPath: string;
}

export interface ReturnShippingBillingCatalogEntry {
  billType: typeof BillType.ReturnShipping;
  product: ReturnShippingCatalogProduct;
  id: `return_shipping:${ReturnShippingCatalogProduct}`;
  label: string;
  billedBy: BillingCatalogBilledBy;
  listed: boolean;
  adminPath: string;
}

export type BillingCatalogEntry =
  | SoftwareBillingCatalogEntry
  | ReturnShippingBillingCatalogEntry;

function software(
  product: ServiceInvoiceType,
  label: string,
  adminPath: string,
  extras?: { listed?: boolean; billedBy?: BillingCatalogBilledBy },
): SoftwareBillingCatalogEntry {
  return {
    billType: BillType.Software,
    product,
    id: `software:${product}`,
    label,
    billedBy: extras?.billedBy ?? "engine",
    listed: extras?.listed ?? true,
    adminPath,
  };
}

function returnShipping(
  product: ReturnShippingCatalogProduct,
  label: string,
  adminPath: string,
): ReturnShippingBillingCatalogEntry {
  return {
    billType: BillType.ReturnShipping,
    product,
    id: `return_shipping:${product}`,
    label,
    billedBy: "payout",
    listed: true,
    adminPath,
  };
}

export const SOFTWARE_BILLING_CATALOG = {
  [ServiceInvoiceType.MARKETING_EMAIL]: software(
    ServiceInvoiceType.MARKETING_EMAIL,
    "Email",
    "marketing",
  ),
  [ServiceInvoiceType.MARKETING_SMS]: software(
    ServiceInvoiceType.MARKETING_SMS,
    "SMS",
    "marketing",
  ),
  [ServiceInvoiceType.ORDER_TRACKING]: software(
    ServiceInvoiceType.ORDER_TRACKING,
    "Order Tracking",
    "orders",
  ),
  [ServiceInvoiceType.RETURN_AI_EXCHANGE]: software(
    ServiceInvoiceType.RETURN_AI_EXCHANGE,
    "AI Exchange",
    "returns",
  ),
  [ServiceInvoiceType.RETURNS]: software(
    ServiceInvoiceType.RETURNS,
    "Returns subscription",
    "returns",
  ),
  [ServiceInvoiceType.CHECKOUT_OPTIMIZATION]: software(
    ServiceInvoiceType.CHECKOUT_OPTIMIZATION,
    "Checkout Optimization",
    "conversion",
  ),
  [ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION]: software(
    ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION,
    "Checkout Optimization (subscription)",
    "conversion",
  ),
  [ServiceInvoiceType.AEO]: software(ServiceInvoiceType.AEO, "AEO", "aeo"),
  [ServiceInvoiceType.IMS]: software(ServiceInvoiceType.IMS, "IMS", "ims"),
  [ServiceInvoiceType.IMS_FORECASTING]: software(
    ServiceInvoiceType.IMS_FORECASTING,
    "IMS Forecasting",
    "ims",
  ),
  [ServiceInvoiceType.RECOVER]: software(
    ServiceInvoiceType.RECOVER,
    "Recover",
    "marketing",
  ),
  [ServiceInvoiceType.DISPUTES]: software(
    ServiceInvoiceType.DISPUTES,
    "Disputes",
    "disputes",
  ),
  [ServiceInvoiceType.DISPUTES_ALERTS_DEFLECTIONS]: software(
    ServiceInvoiceType.DISPUTES_ALERTS_DEFLECTIONS,
    "Disputes alerts",
    "disputes",
  ),
  [ServiceInvoiceType.LCG_COMMIT]: software(
    ServiceInvoiceType.LCG_COMMIT,
    "LCG",
    "landed-cost",
  ),
  [ServiceInvoiceType.CTX_USAGE]: software(
    ServiceInvoiceType.CTX_USAGE,
    "Contextual",
    "conversion",
  ),
  [ServiceInvoiceType.SUPPORT_AI_USAGE]: software(
    ServiceInvoiceType.SUPPORT_AI_USAGE,
    "Support AI",
    "customer-support",
  ),
  [ServiceInvoiceType.DIMENSIONAL_PACKAGING]: software(
    ServiceInvoiceType.DIMENSIONAL_PACKAGING,
    "Dimensional Packaging",
    "conversion",
  ),
  [ServiceInvoiceType.WMS]: software(
    ServiceInvoiceType.WMS,
    "WMS",
    "outbound-labels",
    { listed: false },
  ),
  [ServiceInvoiceType.OMS]: software(
    ServiceInvoiceType.OMS,
    "OMS",
    "outbound-labels",
    { listed: false },
  ),
  [ServiceInvoiceType.SUPPORT_BASE]: software(
    ServiceInvoiceType.SUPPORT_BASE,
    "Support base",
    "customer-support",
  ),
  [ServiceInvoiceType.SUPPORT_AI_SUBSCRIPTION]: software(
    ServiceInvoiceType.SUPPORT_AI_SUBSCRIPTION,
    "Support AI subscription",
    "customer-support",
    { listed: false, billedBy: "legacy_cron" },
  ),
  [ServiceInvoiceType.SUPPORT_VOICE_SUBSCRIPTION]: software(
    ServiceInvoiceType.SUPPORT_VOICE_SUBSCRIPTION,
    "Support voice subscription",
    "customer-support",
    { listed: false, billedBy: "legacy_cron" },
  ),
  [ServiceInvoiceType.SUPPORT_VOICE_USAGE]: software(
    ServiceInvoiceType.SUPPORT_VOICE_USAGE,
    "Support voice usage",
    "customer-support",
    { listed: false, billedBy: "legacy_cron" },
  ),
} as const satisfies Record<ServiceInvoiceType, SoftwareBillingCatalogEntry>;

export const RETURN_SHIPPING_BILLING_CATALOG = {
  [ReturnShippingCatalogProduct.ReturnsCoverage]: returnShipping(
    ReturnShippingCatalogProduct.ReturnsCoverage,
    "Returns coverage",
    "returns",
  ),
  [ReturnShippingCatalogProduct.PackageProtection]: returnShipping(
    ReturnShippingCatalogProduct.PackageProtection,
    "Package protection",
    "returns",
  ),
  [ReturnShippingCatalogProduct.PackageProtectionPlus]: returnShipping(
    ReturnShippingCatalogProduct.PackageProtectionPlus,
    "Package protection plus",
    "returns",
  ),
  [ReturnShippingCatalogProduct.FinalSaleReturns]: returnShipping(
    ReturnShippingCatalogProduct.FinalSaleReturns,
    "Final sale returns",
    "returns",
  ),
  [ReturnShippingCatalogProduct.Warranty]: returnShipping(
    ReturnShippingCatalogProduct.Warranty,
    "Warranty",
    "warranties",
  ),
  [ReturnShippingCatalogProduct.Rmr]: returnShipping(
    ReturnShippingCatalogProduct.Rmr,
    "Managed returns",
    "managed-returns",
  ),
} as const satisfies Record<
  ReturnShippingCatalogProduct,
  ReturnShippingBillingCatalogEntry
>;

export function listBillingCatalog(): BillingCatalogEntry[] {
  return [
    ...Object.values(SOFTWARE_BILLING_CATALOG),
    ...Object.values(RETURN_SHIPPING_BILLING_CATALOG),
  ];
}

export function listedBillingCatalog(): BillingCatalogEntry[] {
  return listBillingCatalog().filter((entry) => entry.listed);
}

export function softwareCatalogEntry(
  product: ServiceInvoiceType,
): SoftwareBillingCatalogEntry {
  return SOFTWARE_BILLING_CATALOG[product];
}

export function returnShippingCatalogEntry(
  product: ReturnShippingCatalogProduct,
): ReturnShippingBillingCatalogEntry {
  return RETURN_SHIPPING_BILLING_CATALOG[product];
}

const returnShippingCatalogProducts = new Set<string>(
  Object.values(ReturnShippingCatalogProduct),
);

export function isReturnShippingCatalogProduct(
  value: string,
): value is ReturnShippingCatalogProduct {
  return returnShippingCatalogProducts.has(value);
}
